import { storeWrapper } from '../storeWrapper'
import { formatDate, dateIsInThePast } from '@repo/utils/time'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { ospreyUpcomingDatesSchema } from '@repo/validation/schema_ospreyAPI'
import { debounce } from '@repo/utils/debounce'

const initialState = {
  dashboard: {
    upcomingDates: {},
    IncludeMattersForReview: true,
    IncludeMattersForAllUsers: false,
    KeyDateAddedByUser: true,
    KeyDateAssignedToUser: true,
    KeyDateRedirectedToUser: true,
    KeyDatesForSupervisingFeeEarner: false,
    IncludeKeyDatesForAllUsers: false,
    StartAtIndex: 0,
    PageSize: 50,
    upcomingDatesModeLabel: 'All',
    upcomingDatesModeValue: 'all'
  },
  workspace: {
    upcomingDates: {},
    IncludeMattersForReview: true,
    IncludeMattersForAllUsers: true,
    KeyDateAddedByUser: true,
    KeyDateAssignedToUser: true,
    KeyDateRedirectedToUser: true,
    KeyDatesForSupervisingFeeEarner: false,
    IncludeKeyDatesForAllUsers: true,
    StartAtIndex: 0,
    PageSize: 50,
    upcomingDatesModeLabel: 'All',
    upcomingDatesModeValue: 'all',
    ClientNo: '',
    MatterNo: 0
  }
}

const groupByDate = (items = []) => {
  const upcomingDates = {}

  items.forEach((item) => {
    let timelineDate

    if (item.upcomingDateType === 'KeyDate') {
      timelineDate = item.keyDate
    } else if (item.upcomingDateType === 'Matter') {
      timelineDate = item.inferredMatterReviewDate || item.matterReviewDate
    } else {
      return
    }

    const isOverdue = dateIsInThePast(timelineDate)
    // group overdue items under 'a past date' key so they are in the right order in the object
    const timelineDateRelative = isOverdue ? '19700101' : formatDate(timelineDate, 'yyyyMMdd')

    if (!upcomingDates[timelineDateRelative]) {
      upcomingDates[timelineDateRelative] = []
    }

    upcomingDates[timelineDateRelative].push(item)
  })

  // sort items by date
  return upcomingDates
}

export const useUpcomingDatesStore = storeWrapper('useUpcomingDatesStore', (set, get) => ({
  ...initialState,
  fetchUpcomingDates: async ({ isMatterSpecific = false }) => {
    debounce({
      id: `fetchUpcomingDates`,
      delay: 300,
      callback: async () => {
        const mode = isMatterSpecific ? 'workspace' : 'dashboard'
        const store = get()
        const settings = store[mode]

        const {
          IncludeMattersForReview,
          IncludeMattersForAllUsers,
          KeyDateAddedByUser,
          KeyDateAssignedToUser,
          KeyDateRedirectedToUser,
          KeyDatesForSupervisingFeeEarner,
          IncludeKeyDatesForAllUsers,
          StartAtIndex,
          PageSize,
          upcomingDatesModeValue
        } = settings

        let data = {
          IncludeMattersForReview,
          IncludeMattersForAllUsers,
          KeyDateAddedByUser,
          KeyDateAssignedToUser,
          KeyDateRedirectedToUser,
          KeyDatesForSupervisingFeeEarner,
          IncludeKeyDatesForAllUsers,
          StartAtIndex,
          PageSize
        }

        // from the matter workspace tab, we want to filter results to a certain matter and show results for all users
        if (isMatterSpecific) {
          const { ClientNo, MatterNo } = get().workspace
          if (!ClientNo) throw 'Client not defined'
          if (!MatterNo && MatterNo !== 0) throw 'Matter not defined'

          data.ClientNo = ClientNo
          data.MatterNo = MatterNo
        }

        switch (upcomingDatesModeValue) {
          case 'matterReviews':
            data = {
              ...data,
              IncludeMattersForReview: true,
              KeyDateAddedByUser: false,
              KeyDateAssignedToUser: false,
              KeyDateRedirectedToUser: false,
              KeyDatesForSupervisingFeeEarner: false,
              IncludeKeyDatesForAllUsers: false
            }
            break
          case 'keyDates':
            data = { ...data, IncludeMattersForReview: false, IncludeMattersForAllUsers: false }
            break
        }

        const fetchedUpcomingDates = await ospreyAPI({
          service: 'matters',
          endpoint: 'KeyDate/GetAllUpcomingDates',
          data,
          schema: ospreyUpcomingDatesSchema
        })

        const groupedData = groupByDate(fetchedUpcomingDates?.rows || [])

        if (mode === 'dashboard') {
          let { dashboard } = get()
          dashboard.upcomingDates = {}
          Object.keys(dashboard.upcomingDates).forEach((key) => delete dashboard.upcomingDates[key])
          dashboard = { ...dashboard, upcomingDates: groupedData }
          set({ dashboard })
        } else {
          let { workspace } = get()
          workspace.upcomingDates = {}
          Object.keys(workspace.upcomingDates).forEach((key) => delete workspace.upcomingDates[key])
          workspace = { ...workspace, upcomingDates: groupedData }
          set({ workspace })
        }
      }
    })
  },
  setUpcomingDatesMode: ({ isMatterSpecific = false, upcomingDatesModeLabel, upcomingDatesModeValue }) => {
    const mode = isMatterSpecific ? 'workspace' : 'dashboard'
    const newParams = { upcomingDatesModeLabel, upcomingDatesModeValue, StartAtIndex: 0, PageSize: 50 }

    if (mode === 'dashboard') {
      let { dashboard } = get()
      dashboard = { ...dashboard, ...newParams }
      set({ dashboard })
    } else {
      let { workspace } = get()
      workspace = { ...workspace, ...newParams }
      set({ workspace })
    }

    get().fetchUpcomingDates({ isMatterSpecific })
  },
  setClientMatter: ({ clientNo, matterNo }) => {
    let { workspace } = get()

    // clear any dates from the previously selected matter
    delete workspace.upcomingDates

    workspace = { ...workspace, upcomingDates: {}, ClientNo: clientNo, MatterNo: matterNo }
    set({ workspace })
  },
  setFilterOptions: ({
    isMatterSpecific = false,
    IncludeMattersForReview,
    KeyDateAddedByUser,
    KeyDateAssignedToUser,
    KeyDateRedirectedToUser,
    KeyDatesForSupervisingFeeEarner
  }) => {
    const mode = isMatterSpecific ? 'workspace' : 'dashboard'
    const newParams = {
      IncludeMattersForReview,
      KeyDateAddedByUser,
      KeyDateAssignedToUser,
      KeyDateRedirectedToUser,
      KeyDatesForSupervisingFeeEarner,
      StartAtIndex: 0,
      PageSize: 50
    }

    const { dashboard, workspace } = get()

    if (mode === 'dashboard') {
      set({ dashboard: { ...dashboard, ...newParams } })
    } else {
      set({ workspace: { ...workspace, ...newParams } })
    }

    get().fetchUpcomingDates({ isMatterSpecific })
  },
  reset: () => {
    const store = get()
    delete store.upcomingDates
    delete store.IncludeMattersForReview
    delete store.KeyDateAddedByUser
    delete store.KeyDateAssignedToUser
    delete store.KeyDateRedirectedToUser
    delete store.KeyDateAssignedToUser
    delete store.KeyDatesForSupervisingFeeEarner
    delete store.IncludeKeyDatesForAllUsers
    delete store.IncludeMatterForAllUsers
    delete store.upcomingDatesModeLabel
    delete store.upcomingDatesModeValue
    delete store.PageSize
    delete store.StartAtIndex
    delete store.dashboard.upcomingDates
    delete store.workspace.upcomingDates

    store.dashboard.upcomingDates = {}
    store.workspace.upcomingDates = {}

    // don't overwrite the upcoming date filter options, so they persist between sessions
    set({
      ...store
    })
  }
}))
